import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { PageHeader, SearchBar, FilterChips, LoaderBlock, EmptyState } from '../components/listHelpers';

const EVENT_CATEGORIES = [
  'Afrobeat Party',
  'Networking Mixer',
  'Cultural Festival',
  'Church/Social Gathering',
  'Professional Event',
  'Food & Lifestyle',
  'Comedy Show',
  'Music Concert',
];

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' });
}

function EventCard({ event }) {
  return (
    <Link
      to={`/app/events/${event.id}`}
      data-testid={`event-card-${event.id}`}
      className="bg-white border border-[#F4F1DE] rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all"
    >
      <div className="relative aspect-[16/9] bg-[#FFE5DD]">
        {event.image_url ? (
          <img src={event.image_url} alt={event.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Calendar className="w-12 h-12 text-[#C8553D]/30" />
          </div>
        )}
        {event.is_featured && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-[#C8553D] text-white text-[9px] font-bold rounded uppercase flex items-center gap-1">
            <Sparkles className="w-2.5 h-2.5" />
            Featured
          </span>
        )}
        <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-[11px] font-bold">
          {formatEventDate(event.date)}
        </div>
      </div>
      <div className="p-4">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#C8553D]">{event.category}</p>
        <h3 className="text-lg font-serif font-bold text-[#2B2D42] line-clamp-1 mt-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          {event.title}
        </h3>
        <p className="text-xs text-[#6D6875] flex items-center gap-1 mt-1">
          <MapPin className="w-3 h-3" /> {event.venue}, {event.city}
        </p>
        <div className="flex items-center justify-between mt-3 text-xs text-[#6D6875]">
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" /> {event.time}
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-3 h-3" /> {(event.going_count || 0) + (event.interested_count || 0)} attending
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [city, setCity] = useState('');

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (search) params.set('search', search);
        if (category) params.set('category', category);
        if (city) params.set('city', city);
        const data = await api(`/events?${params.toString()}`);
        setEvents(data.events || []);
      } catch (err) {
        toast.error(err.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, category, city]);

  const cities = useMemo(() => {
    const s = new Set();
    events.forEach((e) => e.city && s.add(e.city));
    return Array.from(s).sort();
  }, [events]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="events-page">
      <PageHeader title="Events" subtitle="Cultural festivals, mixers & celebrations" testId="events-header" />

      <SearchBar value={search} onChange={setSearch} placeholder="Search events, organizers, venues..." testId="events-search" />

      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6875]">Category</p>
        <FilterChips items={EVENT_CATEGORIES} value={category} onChange={setCategory} testIdPrefix="events-cat" />
      </div>

      {cities.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#6D6875]">City</p>
          <FilterChips items={cities} value={city} onChange={setCity} testIdPrefix="events-city" />
        </div>
      )}

      {loading ? (
        <LoaderBlock testId="events-loading" />
      ) : events.length === 0 ? (
        <EmptyState icon={Calendar} title="No events found" sub="Try a different filter or check back soon." testId="events-empty" />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2" data-testid="events-grid">
          {events.map((e) => (
            <EventCard key={e.id} event={e} />
          ))}
        </div>
      )}
    </div>
  );
}
